import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { astrologyService } from '../services/astrologyService';
import './Chat.css';

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Array<{id: string, text: string, sender: 'user' | 'bot', timestamp: Date}>>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLLMLoading, setIsLLMLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      text: inputMessage.trim(),
      sender: 'user' as const,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);
    setError('');

    try {
      const botMessageId = (Date.now() + 1).toString();
      let responseText = '';
      
      // Add empty bot message placeholder
      setMessages(prev => [...prev, {
        id: botMessageId,
        text: '',
        sender: 'bot' as const,
        timestamp: new Date()
      }]);

      await astrologyService.chat({
        question: userMessage.text
      }, (chunk) => {
        responseText += chunk;
        setMessages(prev => prev.map(msg => 
          msg.id === botMessageId ? { ...msg, text: responseText } : msg
        ));
      });

    } catch (err: any) {
      console.error('Chat Error:', err);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: err.response?.data?.detail || err.message || 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
        sender: 'bot' as const,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      
      // Set error state for UI display
      setError(err.message || 'Error connecting to chat service. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      // Max height of 150px before scrolling
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  };



  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const suggestedQuestions = [
    "What does my zodiac sign say about my personality?",
    "How can I improve my relationship with my partner?",
    "What do the lines on my palm mean?",
    "Tell me about my career prospects this month",
    "How compatible are Aries and Leo?",
    "What should I know about Mercury retrograde?",
    "Generate a horoscope for today",
    "What's my lucky color this week?"
  ];



  return (
    <div className="chat-page">
      {/* Chat Container */}
      <div className="chat-container">
        {/* Chat Header */}
        <div className="chat-header-info">
          <div className="bot-info">
            <div className="bot-avatar">🔮</div>
            <div className="bot-details">
              <h3>AstroML Bot</h3>
              <span className="bot-status">
                Online • AI Assistant
              </span>
            </div>
          </div>
          <div className="chat-actions">
            <button className="clear-chat-btn" onClick={() => setMessages([])}>
              Clear Chat
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="messages-area">
          {messages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🔮</div>
              <h3>Welcome to AstroML Chat!</h3>
              <p>
                I'm your personal AI astrology assistant. Ask me about:
              </p>
              <div className="suggestions-grid">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="suggestion-chip"
                    onClick={() => setInputMessage(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.sender === 'user' ? 'user' : 'bot'}`}
                >
                  <div className={`message-bubble ${message.sender === 'bot' ? 'markdown-content' : ''}`}>
                    {message.sender === 'bot' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.text}
                      </ReactMarkdown>
                    ) : (
                      message.text
                    )}
                  </div>
                  <span className="message-timestamp">{formatTime(message.timestamp)}</span>
                </div>
              ))}
              {isLoading && (
                <div className="message bot">
                  <div className="message-bubble typing">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="typing-text">AI is thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Input Area */}
        <div className="input-area">
          <div className="input-form">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your horoscope, palmistry, compatibility..."
              className="message-input"
              rows={2}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="send-btn"
            >
              {isLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
